import React from 'react';

const fontOptions = [
  'Inter',
  'Cormorant Garamond',
  'Space Grotesk',
  'Unbounded',
  'Playfair Display',
  'Lora',
  'Montserrat',
  'Raleway',
  'Poppins',
  'DM Sans',
  'DM Serif Display',
  'Libre Baskerville',
];

const colorPresets = [
  '#00D9C0',
  '#e32c74',
  '#FF6B35',
  '#FFD700',
  '#8553D1',
  '#2563EB',
  '#059669',
  '#0891B2',
  '#000000',
  '#D8A45D',
  '#4F46E5',
  '#DC2626',
];

function DesignPanel({
  designPanelOpen,
  setDesignPanelOpen,
  editableSettings,
  hasUnsavedChanges,
  savingDesign,
  canUndo,
  canRedo,
  onDesignChange,
  onSaveDesign,
  onDiscardChanges,
  onUndo,
  onRedo,
}) {
  if (!editableSettings) return null;

  return (
    <>
      <button
        className="showcase-design-toggle"
        onClick={() => setDesignPanelOpen((prev) => !prev)}
        title="Design settings"
      >
        <svg
          viewBox="0 0 24 24"
          fill="none"
          stroke="#000"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="M12 19l7-7 3 3-7 7-3-3z" />
          <path d="M18 13l-1.5-7.5L2 2l3.5 14.5L13 18l5-5z" />
          <path d="M2 2l7.586 7.586" />
          <circle cx="11" cy="11" r="2" />
        </svg>
        {hasUnsavedChanges && <span className="unsaved-dot" />}
      </button>

      {designPanelOpen && (
        <div className="showcase-design-panel">
          <div className="showcase-design-panel-header">
            <h3>Design</h3>
            <div className="showcase-design-header-actions">
              <button
                className="showcase-undo-btn"
                onClick={onUndo}
                disabled={!canUndo}
                title="Undo (Ctrl+Z)"
              >
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <polyline points="1 4 1 10 7 10" />
                  <path d="M3.51 15a9 9 0 1 0 2.13-9.36L1 10" />
                </svg>
              </button>
              <button
                className="showcase-redo-btn"
                onClick={onRedo}
                disabled={!canRedo}
                title="Redo (Ctrl+Shift+Z)"
              >
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <polyline points="23 4 23 10 17 10" />
                  <path d="M20.49 15a9 9 0 1 1-2.13-9.36L23 10" />
                </svg>
              </button>
              <button
                className="showcase-design-panel-close"
                onClick={() => setDesignPanelOpen(false)}
              >
                <svg
                  width="18"
                  height="18"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
            </div>
          </div>

          <div className="showcase-design-panel-body">
            {/* Template */}
            <div className="showcase-design-field">
              <label>Template</label>
              <select
                className="showcase-design-select"
                value={editableSettings.theme || 'bold'}
                onChange={(e) => onDesignChange('theme', e.target.value)}
              >
                <option value="bold">Bold</option>
                <option value="clean">Clean</option>
                <option value="creative">Creative</option>
                <option value="pop">Pop</option>
              </select>
            </div>

            {/* Accent Color */}
            <div className="showcase-design-field">
              <label>Accent Color</label>
              <div className="showcase-color-swatches">
                {colorPresets.map((color) => (
                  <button
                    key={color}
                    className={`showcase-color-swatch${(editableSettings.accent_color || '#00D9C0') === color ? ' active' : ''}`}
                    style={{ backgroundColor: color }}
                    onClick={() => onDesignChange('accent_color', color)}
                  />
                ))}
              </div>
              <div className="showcase-color-custom">
                <input
                  type="color"
                  value={editableSettings.accent_color || '#00D9C0'}
                  onChange={(e) => onDesignChange('accent_color', e.target.value)}
                  className="showcase-color-input"
                />
                <input
                  type="text"
                  value={editableSettings.accent_color || '#00D9C0'}
                  onChange={(e) => {
                    const v = e.target.value;
                    if (/^#[0-9A-Fa-f]{0,6}$/.test(v)) onDesignChange('accent_color', v);
                  }}
                  className="showcase-color-hex"
                  placeholder="#000000"
                  maxLength={7}
                />
              </div>
            </div>

            {/* Typography */}
            <div className="showcase-design-field">
              <label>Heading Font</label>
              <select
                className="showcase-design-select"
                value={editableSettings.heading_font || 'Inter'}
                onChange={(e) => onDesignChange('heading_font', e.target.value)}
              >
                {fontOptions.map((f) => (
                  <option key={f} value={f} style={{ fontFamily: f }}>
                    {f}
                  </option>
                ))}
              </select>
            </div>

            <div className="showcase-design-field">
              <label>Body Font</label>
              <select
                className="showcase-design-select"
                value={editableSettings.body_font || 'Inter'}
                onChange={(e) => onDesignChange('body_font', e.target.value)}
              >
                {fontOptions.map((f) => (
                  <option key={f} value={f} style={{ fontFamily: f }}>
                    {f}
                  </option>
                ))}
              </select>
            </div>

            <div className="showcase-design-field">
              <label>Heading Size — {editableSettings.heading_font_size || 32}px</label>
              <div className="showcase-design-slider-row">
                <input
                  type="range"
                  className="showcase-design-slider"
                  min="20"
                  max="60"
                  value={editableSettings.heading_font_size || 32}
                  onChange={(e) => onDesignChange('heading_font_size', parseInt(e.target.value))}
                />
                <span className="showcase-design-slider-value">
                  {editableSettings.heading_font_size || 32}px
                </span>
              </div>
            </div>

            <div className="showcase-design-field">
              <label>Body Size — {editableSettings.body_font_size || 16}px</label>
              <div className="showcase-design-slider-row">
                <input
                  type="range"
                  className="showcase-design-slider"
                  min="12"
                  max="24"
                  value={editableSettings.body_font_size || 16}
                  onChange={(e) => onDesignChange('body_font_size', parseInt(e.target.value))}
                />
                <span className="showcase-design-slider-value">
                  {editableSettings.body_font_size || 16}px
                </span>
              </div>
            </div>
          </div>

          {hasUnsavedChanges && (
            <div className="showcase-design-actions">
              <button
                className="showcase-design-save"
                onClick={onSaveDesign}
                disabled={savingDesign}
              >
                {savingDesign ? 'Saving...' : 'Save'}
              </button>
              <button className="showcase-design-discard" onClick={onDiscardChanges}>
                Discard
              </button>
            </div>
          )}
        </div>
      )}
    </>
  );
}

export { fontOptions, colorPresets };
export default DesignPanel;
